<template>
  <XCard class="py-6 px-4 mb-24">
    <div class="py-2 px-3 text-center mb-4">
      <div class="font-semibold text-lg mb-2">
        {{ scratchAndWinConfig.title }}
      </div>
      <div>
        {{ scratchAndWinConfig.subtitle }}
      </div>
    </div>

    <div>
      <div v-if="loading">
        <img
          :src="images.loadingImage"
          alt="Loading image"
          class="block w-auto mx-auto"
          style="max-height: 300px;">
      </div>

      <div v-else-if="hasWon">
        <img
          :src="images.wonImage"
          alt="Winner image"
          class="block w-auto mx-auto"
          style="max-height: 300px;">
      </div>

      <div v-else-if="hasWon === false">
        <img
          :src="images.lostImage"
          alt="Loser image"
          class="block w-auto mx-auto"
          style="max-height: 300px;">
      </div>

      <div
        v-else
        class="inline-flex flex-wrap items-center justify-center w-full sm:py-2 sm:px-3">
        <XInput
          v-model="code"
          v-validate="'required'"
          data-vv-name="code"
          :class="$style['code-input']"
          :invalid="validationHasErrors('code')" />
        <XButton
          color="primary"
          :class="$style['submit']"
          :loading="loading"
          @click="onCheckScratchAndWinCode">
          {{ $t('event.timeline.scratchAndWin.buttonActions.check') }}
        </XButton>
      </div>

      <div
        v-if="!loading"
        class="mt-1 text-center">
        <div
          v-if="validationErrors('code')[0]"
          class="text-xs text-danger-40">
          {{ validationErrors('code')[0] }}
        </div>

        <div
          v-if="errorMessage"
          class="mt-3 text-danger-50">
          {{ errorMessage }}
        </div>

        <div
          v-if="message"
          class="mt-6 text-info-50 font-semibold md:px-12">
          {{ message }}
        </div>
      </div>

      <div
        v-if="hasWon !== null && !loading"
        class="text-center mt-6">
        <XButton
          color="primary"
          @click="reset">
          {{ $t('event.timeline.scratchAndWin.buttonActions.reset') }}
        </XButton>
      </div>
    </div>
  </XCard>
</template>

<script>
import gql from 'graphql-tag'
import { mapState } from 'vuex'

import FormValidation from '@common/mixins/FormValidation'

export default {
  mixins: [FormValidation],

  props: {
    scratchAndWinConfig: {
      type: Object
    }
  },

  data () {
    return {
      code: '',
      errorMessage: '',
      message: '',
      loading: false,
      images: {
        wonImage: 'http://cdn.yumiwi.io/files/thunderbitch/festivales-2019/winnertakesitall.gif',
        lostImage: 'http://cdn.yumiwi.io/files/thunderbitch/festivales-2019/loser-cry.gif',
        loadingImage: 'http://cdn.yumiwi.io/files/thunderbitch/festivales-2019/botttle-turn.gif'
      },
      hasWon: null
    }
  },

  computed: {
    ...mapState({
      attendeeId: state => state.routeParameters.attendee.id
    })
  },

  methods: {
    onCheckScratchAndWinCode () {
      if (this.loading) {
        return
      }

      this.loading = true
      this.hasWon = null
      this.message = ''
      this.errorMessage = ''

      const startTime = new Date()
      let newReaction

      this.validate()
        .then(isValid => {
          if (!isValid) {
            return
          }

          return this.checkScratchAndWinCode()
        })
        .then(result => {
          if (!result) {
            return
          }

          if (result.error) {
            switch (result.error) {
              case 'invalid':
                this.errorMessage = this.$t('event.timeline.scratchAndWin.errors.invalid', { code: this.code })
                break
              case 'already-used':
                this.errorMessage = this.$t('event.timeline.scratchAndWin.errors.alreadyUsed', { code: this.code })
                break
              default:
                this.errorMessage = this.$t('common.notifications.messages.requestError')
                break
            }
          } else {
            newReaction = result.reaction

            this.code = ''
            this.hasWon = !!result.reaction.data.reward.ref
            this.message = this.hasWon
              ? this.scratchAndWinConfig.wonMessage
              : this.scratchAndWinConfig.lostMessage
          }

          return this.waitForAnimation(startTime)
        })
        .then(() => {
          if (newReaction) {
            this.$emit('addReaction', newReaction)
          }
        })
        .catch(() => {
          this.errorMessage = this.$t('common.notifications.messages.requestError')
        })
        .finally(() => {
          this.loading = false
        })
    },

    reset () {
      this.hasWon = null
      this.message = ''
      this.errorMessage = ''
    },

    checkScratchAndWinCode () {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation CheckScratchAndWinCode($attendeeId: Uuid!, $code: String!) {
              attendee(attendeeId: $attendeeId) {
                checkScratchAndWinCode(code: $code) {
                  error,
                  reaction {
                    id
                    type
                    url
                    createdAt
                    data
                  }
                }
              }
            }
          `,
          variables: {
            attendeeId: this.attendeeId,
            code: this.code
          }
        })
        .then(result => result.data.attendee.checkScratchAndWinCode)
    },

    waitForAnimation (startTime) {
      return new Promise(resolve => {
        setTimeout(() => resolve(), 3750 - (new Date() - startTime))
      })
    }
  }
}
</script>

<style lang="scss" module>
.code-input {
  max-width: 250px;

  input {
    border-radius: config('borderRadius.default') 0 0 config('borderRadius.default') !important;
  }
}

.submit {
  min-height: 38px;
  border-radius: 0 config('borderRadius.default') config('borderRadius.default') 0 !important;
}
</style>
